import Stack from "@mui/material/Stack";
import { Typography, Box } from "@mui/material";
import {
  CONDENSED_FONT_FAMILY,
  DISPLAY_FONT_FAMILY,
} from "../constants";
import { BasicTeamSectionPageProps, SubTablesSection } from "./types";
import TeamReportDataTable from "../TeamReportDataTable";
import ShotChartSection from "../PlayerBlobPage/ShotChartSection";
import {
  ACTION_TABLE_HEADER_TOOLTIPS,
  FREQUENCY_HEADERS,
  GAME_SAMPLE_FLOOR,
  PLAYER_ACTION_HEADER_TOOLTIPS,
  REB_TABLE_NUMERAL_HEADERS,
  individualReboundingHeaderTooltips,
} from "../constants";
import { SHOT_QUAL_TS_LABELS, rebTransSideLabels, shotQualLabel } from "../ShotQualLabel";
import { seasonTypeLabel } from "../utils";
import ExplorerTableLink from "../ExplorerTableLink";

const ORB_TRANSITION_D_TITLE = "ORB / Transition D";
const DRB_TRANSITION_O_TITLE = "DRB / Transition O";

// Per-class P&R tables render two-up (class-paired: a class's story reads
// left to right). They carry a reduced column set and no shot-dist bar, so
// half width fits. First name of a pair anchors the grid row; a pair forms only when its second table comes next.
// Defense leads with the types opponents ran at us, PR | Sm-Sm (Trevor 2026-10-19), then pairs each class's
// Coverage (what we play) with its Screen Defenders (who can be targeted).
const PAIRED_SECTIONS: [string, string][] = [
  ["PR Type", "PR Partners"],
  ["Sm-Sm Type", "Sm-Sm Partners"],
  ["PR Type", "Sm-Sm Type"],
  ["PR Coverage", "PR Screen Defenders"],
  ["Sm-Sm Coverage", "Sm-Sm Screen Defenders"],
  // Zone slices are condensed (no ORB/TOV/shot-dist) so they fit two-up on
  // the offense page; defense has no "Zone by action", so its "Zone by
  // start type" stays full width (pairs only form on adjacency).
  ["Zone by start type", "Zone by action"],
  // Reb/trans condensation (2026-09-10): the trimmed shot-type reb table
  // fits beside the trimmed transition table.
  ["Rebounding by shot type", "Transition by type of possession"],
  // Defense zone tables pair like the offense ones (2026-09-15): halves the
  // page tail so hc-defense closes on two printed pages — and a short final
  // block can't get push-fragmented across the wrapper boundary (the
  // muddy-header trigger).
  ["Zone", "Zone by start type"],
  // Both player-shooting tables are narrow — two-up (user call 2026-09-10).
  ["Closeouts", "3PT / Free Throws"],
];

// The class tables' share is of the class's actions, so its head names the class ("% of PR"), and it reads as a grey
// number with no needle (Trevor, 2026-10-10). "% of Poss" in blobs built before TR5.
const classShareDisplay = (label: string) => ({
  headerLabels: { "% of Actions": `% of ${label}`, "% of Poss": `% of ${label}` },
  needleOrdinalColumns: new Set(["% of Actions", "% of Poss"]),
});

// Presentation-only per-section display tweaks (the blob keeps the data):
// By Shot Type rows ARE the shot regions, so the shot-dist block and TOV%
// add nothing there. Class tables draw the overview double-rule under their
// All lead row, and coverage tables also at the screener-scheme (Up/Drop/
// Switch/Blitz) → BH-defense (Under/Push) seam (user calls 2026-08-26).
const SECTION_DISPLAY_OVERRIDES: Record<
  string,
  {
    hideShotDistribution?: boolean;
    hiddenColumns?: string[];
    separatorAfterRows?: string[];
    disableSmallSampleFade?: boolean;
    extraNumeralHeaders?: Set<string>;
    needleOrdinalColumns?: Set<string>;
    pickTypeRows?: boolean;
    headerLabels?: Record<string, import("react").ReactNode>;
  }
> = {
  // Reb/trans condensation (user call 2026-09-10): shot-type reb keeps the
  // process (% of Poss, Avg Crashers) + outcome (margin, ORB%) columns and
  // drops the machinery; transition drops the four factors. The blob keeps
  // every column. Avg Crashers / ORB% carry visible rank numerals here.
  "Rebounding by shot type": {
    hiddenColumns: ["Pts/ORB", "Pts/Miss", "Trans Rate", "Trans PPP"],
    extraNumeralHeaders: REB_TABLE_NUMERAL_HEADERS,
  },
  // The by-type rows are possession starts, not shots: no rim / mid / 3P bar (Trevor, 2026-10-10)
  "Transition by type of possession": {
    hiddenColumns: ["TS%", "ORB%", "TOV%"],
    hideShotDistribution: true,
  },
  Overall: { extraNumeralHeaders: REB_TABLE_NUMERAL_HEADERS },
  "Rebounding by number of crashers": {
    extraNumeralHeaders: REB_TABLE_NUMERAL_HEADERS,
  },
  // Zone slices: small samples are the norm (fade off), condensed columns
  // so the pair fits two-up.
  "Zone by start type": {
    hideShotDistribution: true,
    hiddenColumns: ["ORB%", "TOV%"],
    disableSmallSampleFade: true,
  },
  "Zone by action": {
    hideShotDistribution: true,
    hiddenColumns: ["ORB%", "TOV%"],
    disableSmallSampleFade: true,
  },
  // Type rows show the PickType display names over either stored
  // vocabulary (TR4, Jacob 2026-10-07).
  "PR Type": {
    separatorAfterRows: ["All PR"],
    pickTypeRows: true,
    ...classShareDisplay("PR"),
  },
  "Sm-Sm Type": {
    separatorAfterRows: ["All Sm-Sm"],
    pickTypeRows: true,
    ...classShareDisplay("Sm-Sm"),
  },
  "PR Partners": classShareDisplay("PR"),
  "Sm-Sm Partners": classShareDisplay("Sm-Sm"),
  // Volumes and shares are tendencies: grey numbers, no needle (Trevor, 2026-10-10)
  Closeouts: { needleOrdinalColumns: new Set(["Per 100", "Shot%"]) },
  // Jacob 2026-10-30: the roster's drives over several seasons, read like Top Actions' Drives table
  "Drives by Season": {
    hideShotDistribution: true,
    needleOrdinalColumns: new Set(["Per 100", "Left%", "Right%"]),
  },
  // Shot quality here is expected 3P% (Trevor, 2026-10-10)
  "3PT / Free Throws": {
    headerLabels: { "Shot Qual": shotQualLabel("3P%"), "x3P%": shotQualLabel("3P%") },
    needleOrdinalColumns: new Set(["3PA/100", "FTA/100"]),
  },
  // The share is "% of Actions" since TR5 ("% of Poss" in blobs built before).
  "PR Coverage": {
    separatorAfterRows: ["All PR", "Blitz"],
    needleOrdinalColumns: new Set(["% of Actions", "% of Poss"]),
  },
  "Sm-Sm Coverage": {
    separatorAfterRows: ["All Sm-Sm", "Blitz"],
    needleOrdinalColumns: new Set(["% of Actions", "% of Poss"]),
  },
  // Man/zone rate reads with a visible rank (both perspectives).
  "Vs Zone": { needleOrdinalColumns: new Set(["% of Poss"]) },
  // Defense Zone pairs with Zone by start type — condensed to half width
  // like the offense zone slices.
  Zone: {
    needleOrdinalColumns: new Set(["% of Poss"]),
    hideShotDistribution: true,
    hiddenColumns: ["ORB%", "TOV%"],
    disableSmallSampleFade: true,
  },
};

// Player and duo tables: their rows qualify by the backend's own game floor, not the team tables' GAME_SAMPLE_FLOOR
const PLAYER_SECTIONS = new Set([
  "PR Partners",
  "Sm-Sm Partners",
  "PR Screen Defenders",
  "Sm-Sm Screen Defenders",
  "Individual Rebounding",
]);

// Offense-only needle-ordinal spot: the action-mix share (the defense page's
// copy of the table keeps the plain needle).
const OFFENSE_ONLY_NEEDLE_ORDINALS: Record<string, Set<string>> = {
  // "Per 100" (actions per 100 halfcourt possessions) since TR5
  "By halfcourt offensive action": new Set(["Per 100", "% of Poss"]),
};

// Canonical sub-table order (user call 2026-09-15: iso | obs and
// dho_bh | dho_scr sit side by side in the two-up grid; TR4's drives pair
// with posts). Stored blobs may carry the older order, so presentation
// re-sorts; unknown keys sink to the end in their stored order.
const SUB_TABLE_KEY_ORDER = [
  "pr_bh", "pr_scr", "smsm_bh", "smsm_scr", "iso", "obs", "dho_bh",
  "dho_scr", "post", "drive",
  "pr_bh_def", "smsm_bh_def", "iso_def", "obs_def", "dho_bh_def",
  "dho_scr_def", "post_def",
];
const subTableRank = (key: string) => {
  const i = SUB_TABLE_KEY_ORDER.indexOf(key);
  return i === -1 ? SUB_TABLE_KEY_ORDER.length : i;
};

// ============================================================================
// Main component
// ============================================================================

export default function BasicTeamSectionPage({
  sections,
  sectionNames,
  title,
  shotProfile,
  gameShotProfile,
  shotDistributionContext,
  teamId,
  teamAbbrev,
  season,
  seasonType,
  reportSource,
  isSingleGame = false,
}: BasicTeamSectionPageProps) {
  const updatedTitle = (() => {
    if (title === ORB_TRANSITION_D_TITLE) {
      return "ORB → Transition Defense";
    } else if (title === DRB_TRANSITION_O_TITLE) {
      return "DRB → Transition Offense";
    }
    return title;
  })();

  // Individual rebounding reads differently per reb/trans half (user call
  // 2026-09-15): the section name says which side, and the margin column
  // only belongs on the offensive-rebounding side.
  const sectionTitleOverride = (name: string): string => {
    if (name !== "Individual Rebounding") return name;
    if (title === ORB_TRANSITION_D_TITLE) return "Individual Offensive Rebounding";
    if (title === DRB_TRANSITION_O_TITLE) return "Individual Defensive Rebounding";
    return name;
  };
  // A game's team breakdown rows under 5 possessions (actions, chances) grey and lose their percentile, as on the
  // ownership view (Trevor, 2026-10-10); a season table fades by rank instead. Player and duo tables keep their own
  // qualifier (the backend lists and scores a player-game from 3 in the game: top_actions GAME_MIN_ACTIONS)
  const gameFloor = (sectionName: string) =>
    isSingleGame && !PLAYER_SECTIONS.has(sectionName) ? GAME_SAMPLE_FLOOR : undefined;
  const drbIndRebHiddenColumns =
    title === DRB_TRANSITION_O_TITLE
      ? { hiddenColumns: ["2nd Chance vs Transition Margin"] }
      : {};
  const rebSide =
    title === ORB_TRANSITION_D_TITLE
      ? "orb"
      : title === DRB_TRANSITION_O_TITLE
        ? "drb"
        : undefined;
  // The reb / trans tables mix both teams' numbers: the opponent's columns say "allowed" (Trans Rate allowed, …)
  const rebTransLabels = rebSide ? rebTransSideLabels(rebSide) : undefined;
  // Individual Rebounding's heads define its columns on hover (RV1), per half and scope
  const headerTooltipsFor = (sectionName: string) =>
    sectionName === "Individual Rebounding" && rebSide
      ? individualReboundingHeaderTooltips(rebSide, isSingleGame)
      : ACTION_TABLE_HEADER_TOOLTIPS[sectionName];

  // Overall is always a plain table, never a sub-tables block.
  const firstSectionData = sections["Overall"] as
    | import("../TeamReportDataTable/types").TeamReportDataTableProps
    | undefined;
  const firstRowContext = firstSectionData?.rows?.[0]?.queryContext;
  const isHalfcourtPage =
    title !== ORB_TRANSITION_D_TITLE && title !== DRB_TRANSITION_O_TITLE;
  let remainingNames = sectionNames
    ? sectionNames.filter((name) => name !== "Overall" && sections[name])
    : Object.keys(sections).filter((name) => name !== "Overall");
  // Reb/trans pages: pull Transition up beside the shot-type reb table (the
  // blob orders Individual Rebounding between them); pairs form on adjacency.
  if (
    remainingNames.includes("Rebounding by shot type") &&
    remainingNames.includes("Transition by type of possession")
  ) {
    remainingNames = remainingNames.filter(
      (n) => n !== "Transition by type of possession",
    );
    remainingNames.splice(
      remainingNames.indexOf("Rebounding by shot type") + 1,
      0,
      "Transition by type of possession",
    );
  }

  // Group section names into render units: paired class tables share a
  // two-up grid row; everything else renders full width.
  const renderUnits: string[][] = [];
  for (let i = 0; i < remainingNames.length; i++) {
    const name = remainingNames[i];
    const pair = PAIRED_SECTIONS.find(([first, second]) => first === name && remainingNames[i + 1] === second);
    if (pair) {
      renderUnits.push([name, pair[1]]);
      i++;
    } else {
      renderUnits.push([name]);
    }
  }

  // Page-level context note (season · scope) — rendered once in the title
  // band, not repeated per table.
  const pageContextNote = season
    ? `${season}-${String((Number(season) + 1) % 100).padStart(2, "0")} · ${seasonTypeLabel(
        seasonType,
      )}`
    : undefined;

  return (
    <Stack
      sx={{
        ".MuiTypography-h4, .MuiTypography-h5": {
          fontFamily: '"Roboto Condensed", sans-serif',
        },
      }}
    >
      {/* Page title band: display-face title + kicker rule filling across to
          the context note (season · scope) — the personnel BASELINE ——— element
          at page level, per user direction (once per page, not per table). */}
      <Stack sx={{ px: 2 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: `${DISPLAY_FONT_FAMILY} !important`,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: 0,
              whiteSpace: "nowrap",
            }}
          >
            {updatedTitle}
          </Typography>
          {/* the page's Overall table has no title of its own */}
          <ExplorerTableLink href={firstSectionData?.explorer} />
          <Box
            sx={{ flex: 1, height: "3px", backgroundColor: "#111", ml: 1.5 }}
          />
          {pageContextNote && (
            <Typography
              component="span"
              sx={{
                fontFamily: CONDENSED_FONT_FAMILY,
                fontSize: "0.862rem",
                color: "text.secondary",
                whiteSpace: "nowrap",
              }}
            >
              {pageContextNote}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Stack spacing={4} sx={{ px: 2 }}>
        {/* Overall table (no sub-header) */}
        <Stack sx={{ px: 2 }}>
          <TeamReportDataTable
            {...(firstSectionData ?? { rows: [], headers: [] })}
            shotDistributionContext={shotDistributionContext}
            teamId={teamId}
            teamAbbrev={teamAbbrev}
            season={season}
            seasonType={seasonType}
            perChancePPP
            {...(isHalfcourtPage ? {} : (SECTION_DISPLAY_OVERRIDES["Overall"] ?? {}))}
            headerLabels={rebTransLabels ?? SHOT_QUAL_TS_LABELS}
          />
        </Stack>

        {/* Shot chart + blob shot-profile tables (user call 2026-09-10 —
            moved up from the overview, replacing the possession-share bar
            charts: the hexbin is the page's visual break AND adds data) */}
        {shotProfile !== undefined && teamId && season && (
          <Stack sx={{ px: 2 }}>
            <ShotChartSection
              teamNbaId={teamId}
              perspective={shotDistributionContext ?? "offense"}
              season={Number(season)}
              seasonType={seasonType}
              title={`Shot Chart — ${
                shotDistributionContext === "defense" ? "Defense" : "Offense"
              }`}
              layout="beside"
              profile={shotProfile}
              reportSource={reportSource}
              // halfcourt, as the page's Overall row and its drill-down (the blob's shot_profile is too)
              contextFilters={firstRowContext?.filters}
            />
          </Stack>
        )}
        {shotProfile === undefined && gameShotProfile && teamId && (
          <Stack sx={{ px: 2 }}>
            <ShotChartSection
              teamNbaId={teamId}
              perspective={shotDistributionContext ?? "offense"}
              season={Number(season ?? 0)}
              title={`Shot Chart — ${
                shotDistributionContext === "defense" ? "Defense" : "Offense"
              }`}
              layout="beside"
              gameProfile={gameShotProfile}
            />
          </Stack>
        )}

        {/* Remaining sections with headers and Explorer links.
            Paired class tables share a two-up grid row. */}
        <Stack spacing={4} sx={{ px: 2 }}>
          {renderUnits.map((unit) => {
            const renderSection = (sectionName: string) => {
              const sectionData = sections[sectionName];
              // Sub-table blocks (Top Actions / Action Defenders): a shared
              // header, then one small table per action role, two-up.
              if ((sectionData as SubTablesSection)?.kind === "subTables") {
                const block = sectionData as SubTablesSection;
                return (
                  <Stack key={sectionName} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 0.5,
                        fontFamily: `${DISPLAY_FONT_FAMILY} !important`,
                        textTransform: "uppercase",
                        fontWeight: 700,
                        fontSize: "1.265rem",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {sectionName}
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        columnGap: 4,
                        rowGap: 1.5,
                        alignItems: "start",
                        "@media print": {
                          gridTemplateColumns: "1fr 1fr",
                          columnGap: 2,
                        },
                      }}
                    >
                      {[...block.subTables]
                        .sort((a, b) => subTableRank(a.key) - subTableRank(b.key))
                        .map((sub) => (
                        // No break-inside: avoid in print (stylesheet.scss "Nothing inside a running-header page
                        // is pushed"): a sub-table splits between rows and repeats its column heads
                        <Stack key={sub.key} sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontFamily: CONDENSED_FONT_FAMILY,
                              fontSize: "0.897rem",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              borderBottom: "2px solid #111",
                              mb: 0.25,
                            }}
                          >
                            {sub.title}
                            <ExplorerTableLink href={sub.explorer} fontSize="1rem" />
                          </Typography>
                          <TeamReportDataTable
                            {...sub}
                            note={undefined}
                            hideShotDistribution
                            teamId={teamId}
                            season={season}
                            seasonType={seasonType}
                            perChancePPP
                            disableSmallSampleFade
                            // Top Actions' volumes and shares read as grey numbers, no needle (Trevor, 2026-10-10)
                            needleOrdinalColumns={
                              sectionName === "Top Actions"
                                ? FREQUENCY_HEADERS
                                : undefined
                            }
                            headerTooltips={PLAYER_ACTION_HEADER_TOOLTIPS}
                          />
                          </Stack>
                        ))}
                    </Box>
                    {block.note && (
                      <Typography
                        sx={{
                          fontFamily: CONDENSED_FONT_FAMILY,
                          fontSize: "0.713rem",
                          color: "text.secondary",
                          mt: 0.5,
                        }}
                      >
                        {block.note}
                      </Typography>
                    )}
                  </Stack>
                );
              }
              const tableData = sectionData as import("../TeamReportDataTable/types").TeamReportDataTableProps;

              return (
                <Stack key={sectionName} sx={{ flex: 1, minWidth: 0 }}>
                  {/* Table headers stay small and unobtrusive (user direction:
                      no numbered codes, no rule, no per-table context note —
                      the page band above carries those). */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ mb: 0.5, "@media print": { breakAfter: "avoid" } }}
                  >
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{
                        mb: 0,
                        fontFamily: `${DISPLAY_FONT_FAMILY} !important`,
                        textTransform: "uppercase",
                        fontWeight: 700,
                        fontSize: "1.265rem",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {sectionTitleOverride(sectionName)}
                    </Typography>
                    <ExplorerTableLink href={tableData.explorer} />
                  </Stack>
                  <TeamReportDataTable
                    {...tableData}
                    shotDistributionContext={shotDistributionContext}
                    teamId={teamId}
                    season={season}
                    seasonType={seasonType}
                    perChancePPP
                    singleGame={isSingleGame}
                    smallSampleFloor={gameFloor(sectionName)}
                    {...(SECTION_DISPLAY_OVERRIDES[sectionName] ?? {})}
                    {...(shotDistributionContext === "offense" &&
                    OFFENSE_ONLY_NEEDLE_ORDINALS[sectionName]
                      ? {
                          needleOrdinalColumns:
                            OFFENSE_ONLY_NEEDLE_ORDINALS[sectionName],
                        }
                      : {})}
                    {...(sectionName === "Individual Rebounding"
                      ? drbIndRebHiddenColumns
                      : rebTransLabels
                        ? { headerLabels: rebTransLabels }
                        : {})}
                    headerTooltips={headerTooltipsFor(sectionName)}
                  />
                </Stack>
              );
            };

            if (unit.length === 2) {
              return (
                <Stack
                  key={unit.join("|")}
                  direction="row"
                  spacing={3}
                  // No break-inside: avoid: pushing a pair whole to the next page is what printed HC defense's
                  // tail over the next section (stylesheet.scss, 2026-10-11)
                  sx={{
                    alignItems: "flex-start",
                    "@media print": { gap: 2 },
                  }}
                >
                  {unit.map(renderSection)}
                </Stack>
              );
            }
            return renderSection(unit[0]);
          })}
        </Stack>
      </Stack>

    </Stack>
  );
}
